import React from "react";
import PropTypes from "prop-types";
import { Button, Chip, Stack, Typography } from "@mui/material";
import Iconify from "../iconify/Iconify";
import { downloadRegistrationPdf } from "../../app/api/axios/services/files.service";

function DownloadRegistrationPdfButton({ contractId, contract, variant = "button", size = "medium" }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Check if contract has a registration PDF
  const hasPdf = contract?.registrationConfirmationPdf?.relativePath;

  const handleDownloadClick = async () => {
    if (!contractId) {
      setError({ message: "Keine Vertrags-ID vorhanden" });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Download the PDF using the contract ID
      const response = await downloadRegistrationPdf(contractId);

      // Create a Blob from the PDF data
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      // Generate filename from contract data or use default
      const filename = contract?.registrationConfirmationPdf?.filename
        || `Anmeldebestaetigung_${contractId}.pdf`;

      // Create a download link
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);

      // Trigger the download
      a.click();

      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      setIsLoading(false);
    } catch (error) {
      console.error("PDF download error:", error);
      setError({
        message: error.response?.data?.message || "Fehler beim Herunterladen des PDFs"
      });
      setIsLoading(false);
    }
  };

  // Don't render if no PDF exists
  if (!hasPdf) {
    return null;
  }

  // Render as chip/badge variant (for compact display in forms)
  if (variant === "chip") {
    return (
      <Chip
        icon={<Iconify icon="mdi:file-pdf-box" width={20} />}
        label={isLoading ? "Lädt..." : "Anmeldebestätigung (PDF)"}
        onClick={handleDownloadClick}
        disabled={isLoading}
        color="primary"
        variant="outlined"
        size={size}
        clickable
      />
    );
  }

  // Render as button variant (default)
  return (
    <Stack spacing={1}>
      <Button
        variant="outlined"
        startIcon={isLoading ? null : <Iconify icon="mdi:download" width={20} />}
        onClick={handleDownloadClick}
        disabled={isLoading}
        size={size}
        fullWidth={variant === "fullWidth"}
      >
        {isLoading ? "Lädt..." : "Anmeldebestätigung (PDF) herunterladen"}
      </Button>

      {error && (
        <Typography variant="body2" color="error">
          {error.message}
        </Typography>
      )}

      {contract?.registrationConfirmationPdf?.createdAt && (
        <Typography variant="caption" color="text.secondary">
          Erstellt am: {new Date(contract.registrationConfirmationPdf.createdAt).toLocaleString('de-DE')}
        </Typography>
      )}
    </Stack>
  );
}

DownloadRegistrationPdfButton.propTypes = {
  contractId: PropTypes.string.isRequired,
  contract: PropTypes.object,
  variant: PropTypes.oneOf(["button", "chip", "fullWidth"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

export default DownloadRegistrationPdfButton;
